// Registro y notificación de brechas de seguridad (LOPDP Art. 43-46).
// Sistemas → Gestión de Autenticación → Brechas de seguridad. Solo admin (nivel>=5).
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { Campo, Modal, fieldCls } from './shared';
import { LOCALE } from '@/lib/utils/regional';

interface Brecha {
  id: string; codigo: string; titulo: string; descripcion: string | null; naturaleza: string | null;
  fecha_incidente: string | null; fecha_deteccion: string; fecha_conocimiento_riesgo: string | null;
  categorias_datos: string | null; datos_comprometidos: string | null; n_afectados_aprox: number | null;
  origen: string | null; consecuencias: string | null; medidas_adoptadas: string | null;
  nivel_riesgo: string; requiere_notificar_titulares: boolean; estado: string;
  notif_autoridad: boolean; notif_autoridad_at: string | null; notif_autoridad_ref: string | null;
  notif_arcotel: boolean; notif_titulares: boolean; notif_titulares_medio: string | null;
  comunicacion_publica: boolean; motivo_dilacion: string | null; resolucion: string | null;
  limite_autoridad: string; limite_titulares: string; vencido_autoridad: boolean; vencido_titulares: boolean;
}

const rpc = async <T,>(fn: string, body: Record<string, unknown> = {}): Promise<T> =>
  (await apiClient.post<T>(`/rpc/${fn}`, body)).data;

const NIVELES = [
  { v: 'alto', l: 'Alto' }, { v: 'medio', l: 'Medio' }, { v: 'bajo', l: 'Bajo' },
  { v: 'improbable', l: 'Improbable (sin riesgo)' },
];
const RIESGO_CLS: Record<string, string> = {
  alto: 'bg-urgente-soft text-urgente', medio: 'bg-pendiente-soft text-pendiente',
  bajo: 'bg-estable-soft text-estable', improbable: 'bg-muted text-muted-foreground',
};
const ESTADO_CLS: Record<string, string> = {
  abierta: 'bg-pendiente-soft text-pendiente', en_evaluacion: 'bg-estable-soft text-estable',
  notificada: 'bg-completado-soft text-completado', cerrada: 'bg-muted text-muted-foreground',
};
const diasRestantes = (limite: string) => Math.ceil((new Date(limite).getTime() - Date.now()) / 86400000);

// badge de plazo legal
function Plazo({ label, limite, hecho, vencido, exenta }: { label: string; limite: string; hecho: boolean; vencido: boolean; exenta?: boolean }) {
  if (exenta) return <span className="rounded px-2 py-0.5 text-xs bg-muted text-muted-foreground">{label}: no requerida</span>;
  if (hecho) return <span className="rounded px-2 py-0.5 text-xs bg-completado-soft text-completado">{label}: notificada ✓</span>;
  const d = diasRestantes(limite);
  const cls = vencido ? 'bg-urgente-soft text-urgente font-semibold' : d <= 1 ? 'bg-pendiente-soft text-pendiente' : 'bg-estable-soft text-estable';
  return <span className={`rounded px-2 py-0.5 text-xs ${cls}`}>{label}: {vencido ? `vencida hace ${-d}d` : `${d}d restantes`}</span>;
}

const vacio = {
  id: '', titulo: '', descripcion: '', naturaleza: '', fecha_incidente: '', fecha_deteccion: '',
  fecha_conocimiento_riesgo: '', categorias_datos: '', datos_comprometidos: '', n_afectados_aprox: '',
  origen: '', consecuencias: '', medidas_adoptadas: '', nivel_riesgo: 'medio', requiere_notificar_titulares: true,
  motivo_dilacion: '',
};
type Form = typeof vacio;

export function Brechas() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: rows = [], isFetching, error } = useQuery<Brecha[]>({
    queryKey: ['brechas'], queryFn: () => rpc<Brecha[]>('brechas_listar'),
  });
  const [form, setForm] = useState<Form | null>(null);

  const guardar = useMutation({
    mutationFn: (f: Form) => rpc('brecha_guardar', { _data: { ...f, n_afectados_aprox: f.n_afectados_aprox || null } }),
    onSuccess: () => { toast.success('Brecha guardada'); setForm(null); void qc.invalidateQueries({ queryKey: ['brechas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const notificar = useMutation({
    mutationFn: ({ id, destino, ref, medio }: { id: string; destino: string; ref?: string; medio?: string }) =>
      rpc('brecha_registrar_notificacion', { _id: id, _destino: destino, _ref: ref ?? null, _medio: medio ?? null }),
    onSuccess: () => { toast.success('Notificación registrada'); void qc.invalidateQueries({ queryKey: ['brechas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const cerrar = useMutation({
    mutationFn: ({ id, resolucion }: { id: string; resolucion: string }) => rpc('brecha_cerrar', { _id: id, _resolucion: resolucion || null }),
    onSuccess: () => { toast.success('Brecha cerrada'); void qc.invalidateQueries({ queryKey: ['brechas'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (error) return <div className="p-6 text-sm text-urgente">{getErrorMessage(error)}</div>;

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-primary" /> Brechas de seguridad</h1>
          <p className="text-sm text-muted-foreground">Registro y notificación de vulneraciones de datos personales. Autoridad/ARCOTEL: 5 días · titulares: 3 días (LOPDP Art. 43-46).</p>
        </div>
        <Button onClick={() => setForm({ ...vacio })}><Plus className="w-4 h-4 mr-1" /> Registrar brecha</Button>
      </div>

      {isFetching && <div className="py-6 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
      {!isFetching && rows.length === 0 && <p className="text-sm text-muted-foreground">Sin brechas registradas.</p>}

      <div className="space-y-3">
        {rows.map((b) => (
          <Card key={b.id}>
            <CardContent className="pt-4 space-y-2">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <div className="font-medium flex items-center gap-2">
                    {b.titulo}
                    <span className={`rounded px-2 py-0.5 text-xs ${RIESGO_CLS[b.nivel_riesgo]}`}>{NIVELES.find((n) => n.v === b.nivel_riesgo)?.l ?? b.nivel_riesgo}</span>
                    <span className={`rounded px-2 py-0.5 text-xs ${ESTADO_CLS[b.estado]}`}>{b.estado}</span>
                  </div>
                  <div className="text-xs text-muted-foreground font-mono">{b.codigo} · detectada {new Date(b.fecha_deteccion).toLocaleString(LOCALE, { dateStyle: 'medium', timeStyle: 'short' })}{b.n_afectados_aprox ? ` · ~${b.n_afectados_aprox} afectados` : ''}</div>
                </div>
                <button className="text-xs underline text-muted-foreground hover:text-primary" onClick={() => setForm({
                  id: b.id, titulo: b.titulo, descripcion: b.descripcion ?? '', naturaleza: b.naturaleza ?? '',
                  fecha_incidente: b.fecha_incidente?.slice(0, 16) ?? '', fecha_deteccion: b.fecha_deteccion?.slice(0, 16) ?? '',
                  fecha_conocimiento_riesgo: b.fecha_conocimiento_riesgo?.slice(0, 16) ?? '', categorias_datos: b.categorias_datos ?? '',
                  datos_comprometidos: b.datos_comprometidos ?? '', n_afectados_aprox: b.n_afectados_aprox?.toString() ?? '',
                  origen: b.origen ?? '', consecuencias: b.consecuencias ?? '', medidas_adoptadas: b.medidas_adoptadas ?? '',
                  nivel_riesgo: b.nivel_riesgo, requiere_notificar_titulares: b.requiere_notificar_titulares, motivo_dilacion: b.motivo_dilacion ?? '',
                })}>Editar</button>
              </div>

              {b.estado !== 'cerrada' && (
                <div className="flex flex-wrap items-center gap-2">
                  <Plazo label="Autoridad" limite={b.limite_autoridad} hecho={b.notif_autoridad} vencido={b.vencido_autoridad} exenta={b.nivel_riesgo === 'improbable'} />
                  <Plazo label="Titulares" limite={b.limite_titulares} hecho={b.notif_titulares || b.comunicacion_publica} vencido={b.vencido_titulares} exenta={!b.requiere_notificar_titulares} />
                  {b.notif_arcotel && <span className="rounded px-2 py-0.5 text-xs bg-completado-soft text-completado">ARCOTEL ✓</span>}
                </div>
              )}

              {b.estado !== 'cerrada' && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {!b.notif_autoridad && b.nivel_riesgo !== 'improbable' && (
                    <Button size="sm" variant="outline" onClick={async () => {
                      const v = await dlg.pedir('Notifiqué a la Autoridad', [{ key: 'ref', label: 'Nº de oficio / referencia de la notificación (opcional)', minimo: 0 }], { aceptar: 'Registrar' });
                      notificar.mutate({ id: b.id, destino: 'autoridad', ref: v?.ref || undefined });
                    }}>Notifiqué a la Autoridad</Button>
                  )}
                  {!b.notif_arcotel && b.nivel_riesgo !== 'improbable' && (
                    <Button size="sm" variant="outline" onClick={() => notificar.mutate({ id: b.id, destino: 'arcotel' })}>Notifiqué a ARCOTEL</Button>
                  )}
                  {b.requiere_notificar_titulares && !b.notif_titulares && !b.comunicacion_publica && (
                    <Button size="sm" variant="outline" onClick={async () => {
                      const v = await dlg.pedir('Notifiqué a titulares', [{ key: 'medio', label: 'Medio de notificación a los titulares (correo, llamada, etc.)', minimo: 0 }], { aceptar: 'Registrar' });
                      notificar.mutate({ id: b.id, destino: 'titulares', medio: v?.medio || undefined });
                    }}>Notifiqué a titulares</Button>
                  )}
                  {b.requiere_notificar_titulares && !b.notif_titulares && !b.comunicacion_publica && (
                    <Button size="sm" variant="outline" onClick={async () => { if (await dlg.confirmar('¿Registrar comunicación pública?', { detalle: 'Excepción Art. 46.3, esfuerzo desproporcionado.' })) notificar.mutate({ id: b.id, destino: 'publica' }); }}>Comunicación pública</Button>
                  )}
                  <Button size="sm" variant="outline" onClick={async () => {
                    const v = await dlg.pedir('Cerrar brecha', [{ key: 'resolucion', label: 'Resolución / cierre de la gestión', multilinea: true, minimo: 0 }]);
                    cerrar.mutate({ id: b.id, resolucion: v?.resolucion ?? '' });
                  }}>Cerrar</Button>
                </div>
              )}
              {b.estado === 'cerrada' && b.resolucion && <p className="text-xs text-muted-foreground">Cerrada: {b.resolucion}</p>}
            </CardContent>
          </Card>
        ))}
      </div>

      {form && <BrechaModal form={form} setForm={setForm} onClose={() => setForm(null)} onSave={() => guardar.mutate(form)} saving={guardar.isPending} />}
    </div>
  );
}

function BrechaModal({ form, setForm, onClose, onSave, saving }: { form: Form; setForm: (f: Form) => void; onClose: () => void; onSave: () => void; saving: boolean }) {
  const set = (k: keyof Form, v: string | boolean) => setForm({ ...form, [k]: v });
  return (
    <Modal title={form.id ? 'Editar brecha' : 'Registrar brecha'} onClose={onClose} wide>
      <div className="space-y-3">
        <Campo label="Título *"><input className={fieldCls} value={form.titulo} onChange={(e) => set('titulo', e.target.value)} /></Campo>
        <Campo label="Descripción / naturaleza de la vulneración"><textarea className={`${fieldCls} h-20`} value={form.descripcion} onChange={(e) => set('descripcion', e.target.value)} /></Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Fecha del incidente"><input type="datetime-local" className={fieldCls} value={form.fecha_incidente} onChange={(e) => set('fecha_incidente', e.target.value)} /></Campo>
          <Campo label="Fecha de detección (constancia)"><input type="datetime-local" className={fieldCls} value={form.fecha_deteccion} onChange={(e) => set('fecha_deteccion', e.target.value)} /></Campo>
          <Campo label="Fecha de conocimiento del riesgo"><input type="datetime-local" className={fieldCls} value={form.fecha_conocimiento_riesgo} onChange={(e) => set('fecha_conocimiento_riesgo', e.target.value)} /></Campo>
          <Campo label="Nivel de riesgo">
            <select className={fieldCls} value={form.nivel_riesgo} onChange={(e) => set('nivel_riesgo', e.target.value)}>
              {NIVELES.map((n) => <option key={n.v} value={n.v}>{n.l}</option>)}
            </select>
          </Campo>
          <Campo label="Categorías de datos afectados"><input className={fieldCls} value={form.categorias_datos} onChange={(e) => set('categorias_datos', e.target.value)} /></Campo>
          <Campo label="Nº aproximado de afectados"><input type="number" className={fieldCls} value={form.n_afectados_aprox} onChange={(e) => set('n_afectados_aprox', e.target.value)} /></Campo>
        </div>
        <Campo label="Datos comprometidos"><textarea className={`${fieldCls} h-14`} value={form.datos_comprometidos} onChange={(e) => set('datos_comprometidos', e.target.value)} /></Campo>
        <Campo label="Origen / causa"><textarea className={`${fieldCls} h-14`} value={form.origen} onChange={(e) => set('origen', e.target.value)} /></Campo>
        <Campo label="Consecuencias"><textarea className={`${fieldCls} h-14`} value={form.consecuencias} onChange={(e) => set('consecuencias', e.target.value)} /></Campo>
        <Campo label="Medidas adoptadas"><textarea className={`${fieldCls} h-14`} value={form.medidas_adoptadas} onChange={(e) => set('medidas_adoptadas', e.target.value)} /></Campo>
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.requiere_notificar_titulares} onChange={(e) => set('requiere_notificar_titulares', e.target.checked)} /> Conlleva riesgo a los titulares (requiere notificarles, 3 días)</label>
        <Campo label="Motivo de dilación (si se excede el plazo de 5 días)"><input className={fieldCls} value={form.motivo_dilacion} onChange={(e) => set('motivo_dilacion', e.target.value)} /></Campo>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={saving} disabled={!form.titulo.trim()} onClick={onSave}>Guardar</Button>
        </div>
      </div>
    </Modal>
  );
}
